import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ClientOrg } from '@/api/schemasMembers';
import type { RowAction } from '@/components/RowActions';
import { useOrgActions } from '@/shell/useOrgActions';
import { ReactivateClientDialog, SuspendClientDialog } from './ClientStatusDialogs';
import { clientActions, deletionActionLabel, sectionOfAction, type ClientActionKey } from './clientLifecycleModel';

type Look = Pick<RowAction, 'label' | 'icon' | 'destructive'>;

function lookOf(key: ClientActionKey, o: ClientOrg): Look {
  switch (key) {
    case 'edit': return { label: 'Edit', icon: 'edit' };
    case 'invite': return { label: 'Invite people', icon: 'person_add' };
    case 'suspend': return { label: 'Suspend', icon: 'pause_circle', destructive: true };
    case 'reactivate': return { label: 'Reactivate', icon: 'play_circle' };
    case 'delete': return { label: 'Delete', icon: 'delete_forever', destructive: true };
    case 'deletion': return { label: deletionActionLabel(o.status), icon: o.status === 'deletion_failed' ? 'restart_alt' : 'pending', destructive: o.status === 'deletion_failed' };
    default: return { label: 'Preview as client', icon: 'visibility' };
  }
}

/**
 * The row actions of the Clients list per status (clientLifecycleModel `clientActions`): suspend and reactivate open
 * their dialog here; edit, invite and delete open the client page on the card that does it; preview starts the preview.
 */
export function useClientRowActions() {
  const navigate = useNavigate();
  const { startPreview } = useOrgActions();
  const [suspending, setSuspending] = useState<ClientOrg | null>(null);
  const [reactivating, setReactivating] = useState<ClientOrg | null>(null);
  const run = (key: ClientActionKey, o: ClientOrg) => {
    const section = sectionOfAction(key);
    if (section) void navigate({ to: '/clients/$orgId', params: { orgId: o.id }, search: { section } });
    else if (key === 'suspend') setSuspending(o);
    else if (key === 'reactivate') setReactivating(o);
    else startPreview({ id: o.id, name: o.name, ...(o.status === 'suspended' ? { suspended: true } : {}) });
  };
  const actions = (o: ClientOrg): RowAction[] =>
    clientActions(o.status).map((key) => ({ key, ...lookOf(key, o), testId: `client-${key}-${o.slug}`, onSelect: () => run(key, o) }));
  const dialogs = (
    <>
      <SuspendClientDialog org={suspending} onDone={() => setSuspending(null)} />
      <ReactivateClientDialog org={reactivating} onDone={() => setReactivating(null)} />
    </>
  );
  return { actions, dialogs };
}
